import { useCallback, useState } from 'react';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { get, isEmpty, isFunction, map } from 'lodash';
import { ReactComponentLike } from 'prop-types';
import Styles from './MenuList.module.css';
import { isMicroApp } from '../../Common/Common.utils';

export type Option = {
	title: string;
	icon?: ReactComponentLike;
	canHide?: boolean;
	isDelete?: boolean;
};

export interface MenuListProps {
	anchorTitle?: string;
	anchorIcon?: ReactComponentLike;
	options: Array<Option>;
	label?: string;
	labelClassname?: string;
	selectedKey?: string | number;
	anchorPlacement?: string;

	onAnchorClick?(): void;

	onClose?(): void;

	container?: Element | (() => Element | null) | null | undefined;

	onMenuClick(
		event: React.MouseEvent<HTMLElement>,
		prop: Option,
		onClose: (val: unknown) => void
	): void;
}

const MenuList = ({
	options,
	anchorIcon: AnchorIcon,
	anchorTitle,
	onMenuClick,
	label,
	selectedKey,
	labelClassname,
	anchorPlacement,
	onClose,
	onAnchorClick,
	container,
}: MenuListProps) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		if (isFunction(onAnchorClick)) {
			onAnchorClick();
		}
	};

	const handleClose = (event: React.MouseEvent<HTMLElement>) => {
		event && event.stopPropagation();
		setAnchorEl(null);
		if (isFunction(onClose)) {
			onClose();
		}
	};

	const onClick = useCallback(
		(event: React.MouseEvent<HTMLElement>, item: Option) => {
			event.stopPropagation();
			//@ts-ignore
			onMenuClick(event, item, handleClose);
		},
		[onMenuClick, handleClose]
	);

	return (
		<div
			className={classNames(Styles.wrapper, {
				[Styles.microApp]: isMicroApp,
			})}
		>
			<div className={Styles.anchor} onClick={handleMenu}>
				<Tooltip
					// open={true}
					title={anchorTitle || ''}
					//@ts-ignore
					placement={anchorPlacement || 'bottom'}
					arrow
					classes={{ popper: Styles.tooltip }}
				>
					<div className={classNames(Styles.label, labelClassname)}>
						{AnchorIcon ? (
							<AnchorIcon selectedKey={selectedKey} className={Styles.icon} />
						) : (
							''
						)}
					</div>
				</Tooltip>
			</div>
			{!isEmpty(options) && (
				<Menu
					id="menu-appbar"
					anchorEl={anchorEl}
					disableAutoFocusItem
					classes={{
						paper: Styles.menu,
						root: Styles.menuRoot,
						list: Styles.menuList,
					}}
					container={container}
					PaperProps={{
						elevation: 3,
						sx: {
							overflow: 'visible',
							filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
							mt: 1.5,
							'& .MuiAvatar-root': {
								width: 32,
								height: 32,
								ml: -0.5,
								mr: 1,
							},
							'&:before': {
								content: '""',
								display: 'block',
								position: 'absolute',
								top: 0,
								right: 5,
								width: 10,
								height: 10,
								transform: 'translateY(-50%) rotate(45deg)',
								zIndex: 9,
							},
						},
					}}
					keepMounted
					anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
					transformOrigin={{
						vertical: 160,
						horizontal: 'center',
					}}
					open={Boolean(anchorEl)}
					onClose={handleClose}
				>
					{map(options, (item: Option, i: number) => {
						const Icon = item.icon;
						if (item.canHide) return;

						return (
							<MenuItem
								className={classNames(Styles.menuItem, {
									[Styles.deleteMenuItem]: item.isDelete,
									[Styles.highlight]: get(item, 'value', 0) === selectedKey,
									[Styles.microApp]: isMicroApp,
								})}
								onClick={(e) => {
									onClick(e, item);
								}}
								key={`${item.title}-${i}`}
							>
								{Icon ? <Icon /> : ''}
								{`${Icon ? ' ' : ''}`}
								{item.title}
							</MenuItem>
						);
					})}
				</Menu>
			)}
		</div>
	);
};

export default MenuList;
